import {
  DAY_MINUTES,
  type EventRun,
  lastBoundary,
  nextBoundary,
  type RuleEvery,
  resetEvery,
  runsBetween,
} from '@mogzo/shared';
import type { Schedule, ScheduleEvent, ScheduleRule } from '../lib/schedule';

const DAY_MS = DAY_MINUTES * 60_000;
const SPAN_DAYS = 14;
const DAY_NAMES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

interface Lane {
  key: string;
  name: string;
  kind: string;
  bars: { left: number; width: number; on: boolean; label: string }[];
  marks: { left: number; failed: boolean }[];
}

const scheduleOf = (event: ScheduleEvent) => ({
  repeat: event.repeat,
  startsAt: event.startsAt ? new Date(event.startsAt) : null,
  endsAt: event.endsAt ? new Date(event.endsAt) : null,
  onMinute: event.onMinute,
  offMinute: event.offMinute,
});

/** Each time a rule runs between `from` and `to`, as far as can be told. */
function ruleTimes(rule: ScheduleRule, schedule: Schedule, from: Date, to: Date): Date[] {
  if (!rule.enabled) return [];
  let every: RuleEvery | null = null;
  let minute = 0;
  if (rule.trigger === 'times') {
    every = rule.every;
    minute = rule.atMinute ?? 0;
  } else if (rule.trigger === 'board_reset') {
    const board = schedule.boards.find((b) => b.id === rule.board);
    every = board ? resetEvery(board.reset) : null;
  } else {
    const event = schedule.events.find((e) => e.id === rule.event);
    if (!event?.enabled) return [];
    return runsBetween(scheduleOf(event), new Date(from.getTime() - 90 * DAY_MS), to)
      .map((run) => run.end)
      .filter((end) => end >= from && end <= to);
  }
  if (!every) return [];
  const times: Date[] = [];
  let next = lastBoundary(every, minute, from);
  if (next < from) next = nextBoundary(every, minute, next);
  while (next <= to && times.length < 62) {
    times.push(next);
    next = nextBoundary(every, minute, next);
  }
  return times;
}

/**
 * The next two weeks from this Monday (M37): events as bars (on now, or scheduled), rules as
 * marks where they run, a failed run in red. Times are UTC.
 */
export function ScheduleTimeline({ schedule }: { schedule: Schedule }) {
  const now = new Date(schedule.now);
  const monday = lastBoundary('week', 0, now);
  const end = new Date(monday.getTime() + SPAN_DAYS * DAY_MS);
  const span = end.getTime() - monday.getTime();
  const place = (time: Date) => ((time.getTime() - monday.getTime()) / span) * 100;
  const failed = new Set(
    schedule.runs
      .filter((run) => run.status === 'failed')
      .map((run) => `${run.sourceId}:${run.at}`),
  );

  const lanes: Lane[] = [];
  for (const event of schedule.events) {
    const runs: (EventRun & { test?: boolean })[] = event.enabled
      ? runsBetween(scheduleOf(event), monday, end)
      : [];
    if (event.run?.test)
      runs.push({ start: new Date(event.run.start), end: new Date(event.run.end), test: true });
    if (!runs.length) continue;
    lanes.push({
      key: `event:${event.id}`,
      name: event.name,
      kind: `Event · ${event.repeat === 'once' ? 'once' : event.repeat === 'daily' ? 'every day' : 'every week'}`,
      bars: runs.map((run) => {
        const on = run.start <= now && now < run.end;
        const left = Math.max(0, place(run.start));
        return {
          left,
          width: Math.min(100, place(run.end)) - left,
          on,
          label: `${event.name}${on ? ' · on now' : ''}${run.test ? ' (test)' : ''}`,
        };
      }),
      marks: [],
    });
  }
  for (const rule of schedule.rules) {
    const times = ruleTimes(rule, schedule, monday, end);
    if (!times.length) continue;
    const achievement = schedule.achievements.find((a) => a.id === rule.achievement);
    lanes.push({
      key: `rule:${rule.id}`,
      name:
        rule.action === 'achievement'
          ? (achievement?.name ?? rule.achievement ?? '')
          : (rule.key ?? ''),
      kind: `Rule · ${rule.trigger === 'times' ? `every ${rule.every}` : rule.trigger === 'board_reset' ? 'a board resets' : 'an event ends'}`,
      bars: [],
      marks: times.map((time) => ({
        left: place(time),
        failed: failed.has(`${rule.id}:${time.toISOString()}`),
      })),
    });
  }

  if (!lanes.length) {
    return <p className="m-0 px-5 py-6 text-muted">Nothing is scheduled in the next two weeks.</p>;
  }

  const nowLeft = place(now);
  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-[820px]">
        <div className="flex-[0_0_196px] border-r border-line">
          <div className="h-12" />
          {lanes.map((lane) => (
            <div
              key={lane.key}
              className="flex h-11 flex-col justify-center border-t border-line py-1 pr-3 pl-5"
            >
              <span className="truncate text-[13px] leading-[18px] font-medium">{lane.name}</span>
              <span className="text-xs text-subtle">{lane.kind}</span>
            </div>
          ))}
        </div>
        <div className="relative min-w-0 flex-auto">
          <div className="grid h-12 grid-cols-[repeat(14,minmax(0,1fr))]">
            {Array.from({ length: SPAN_DAYS }, (_, day) => {
              const date = new Date(monday.getTime() + day * DAY_MS);
              const today =
                date.getTime() <= now.getTime() && now.getTime() < date.getTime() + DAY_MS;
              return (
                <div
                  key={date.toISOString()}
                  className={`px-2 pt-2 ${day ? 'border-l border-line' : ''} ${day % 7 >= 5 ? 'bg-field' : ''} ${today ? 'font-semibold text-ink' : 'text-muted'}`}
                >
                  <span className="block text-[11px] leading-4 font-semibold tracking-[0.06em]">
                    {today ? 'TODAY' : DAY_NAMES[day % 7]}
                  </span>
                  <span className="block text-[13px] leading-[18px]">
                    {date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </span>
                </div>
              );
            })}
          </div>
          {lanes.map((lane) => (
            <div
              key={lane.key}
              className="relative h-11 border-t border-line bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px)] bg-[length:calc(100%/14)_100%]"
            >
              {lane.bars.map((bar) => (
                <div
                  key={`${bar.left}`}
                  className={`absolute top-[7px] flex h-[30px] items-center overflow-hidden rounded-[6px] border px-2.5 text-xs font-medium whitespace-nowrap ${
                    bar.on
                      ? 'border-success bg-success-soft text-success'
                      : 'border-info bg-info-soft text-info'
                  }`}
                  style={{ left: `${bar.left}%`, width: `${Math.max(bar.width, 0.6)}%` }}
                  title={bar.label}
                >
                  <span className="truncate">{bar.label}</span>
                </div>
              ))}
              {lane.marks.map((mark) => (
                <span
                  key={`${mark.left}`}
                  aria-hidden="true"
                  className={`absolute top-[17px] size-2.5 rotate-45 ${mark.failed ? 'bg-danger' : 'bg-muted'}`}
                  style={{
                    left: mark.left >= 100 ? 'calc(100% - 12px)' : `calc(${mark.left}% - 5px)`,
                  }}
                />
              ))}
            </div>
          ))}
          <div
            aria-hidden="true"
            className="absolute top-10 bottom-0 w-0.5 bg-ink"
            style={{ left: `${nowLeft}%` }}
          />
          <span
            className="absolute top-[26px] rounded-[4px] bg-card px-1 text-[11px] leading-[14px] font-semibold"
            style={{ left: `calc(${nowLeft}% + 6px)` }}
          >
            Now ·{' '}
            {now.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

/** The timeline's key: what the colours and marks mean. */
export function TimelineKey() {
  const item = 'inline-flex items-center gap-1.5';
  return (
    <div className="flex flex-wrap gap-4 text-[13px] text-muted">
      <span className={item}>
        <span
          aria-hidden="true"
          className="h-2.5 w-3.5 rounded-[3px] border border-success bg-success-soft"
        />
        On now
      </span>
      <span className={item}>
        <span
          aria-hidden="true"
          className="h-2.5 w-3.5 rounded-[3px] border border-info bg-info-soft"
        />
        Scheduled
      </span>
      <span className={item}>
        <span aria-hidden="true" className="size-[9px] rotate-45 bg-muted" />
        Rule runs
      </span>
      <span className={item}>
        <span aria-hidden="true" className="size-[9px] rotate-45 bg-danger" />
        Failed
      </span>
    </div>
  );
}
